import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'utils/notify'
import { FirestoreDatabase } from '../index'
import { Collection, Roles } from 'constants/index'
import { signOut } from 'store/auth'
import { cloudFunctionsInstance } from 'config/axios'

export const fetchAllOrganizers = createAsyncThunk(
  'organizers/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await FirestoreDatabase.collection(Collection.USERS)
        .where('role', '==', Roles.ORGANIZER)
        .get()
      return snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }))
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const createOrganizer = createAsyncThunk(
  'organizers/create',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cloudFunctionsInstance.post('/createConferenceOrganizer', payload)
      toast.success('Conference Organizer account created.')
      if (response.data.isEmailSent) {
        toast.success('Activation email sent to the organizer.')
      } else {
        toast.error('Activation email could not be sent.')
      }
      return response.data
    } catch (err) {
      const msg =
        err.response?.data?.msg || 'Something went wrong creating the organizer. Please try again.'
      toast.error(msg)
      return rejectWithValue(msg)
    }
  }
)

export const resendActivationEmail = createAsyncThunk(
  'organizers/resendActivationEmail',
  async ({ uid, callerUid }, { rejectWithValue }) => {
    try {
      const response = await cloudFunctionsInstance.post('/resendActivationEmail', {
        uid,
        callerUid
      })
      if (response.data.isEmailSent) {
        toast.success('Activation email resent.')
      } else {
        toast.error('Activation email could not be sent.')
      }
      return response.data
    } catch (err) {
      const msg = err.response?.data?.msg || 'Could not resend the activation email.'
      toast.error(msg)
      return rejectWithValue(msg)
    }
  }
)

export const deleteOrganizer = createAsyncThunk(
  'organizers/delete',
  async (uid, { rejectWithValue }) => {
    try {
      const response = await cloudFunctionsInstance.delete(`/deleteConferenceOrganizer?uid=${uid}`)
      toast.success(response.data?.msg || 'Organizer deleted.')
      return { uid }
    } catch (err) {
      const msg =
        err.response?.data?.msg || 'Something went wrong deleting the organizer. Please try again.'
      toast.error(msg)
      return rejectWithValue(msg)
    }
  }
)

export const updateOrganizer = createAsyncThunk(
  'organizers/update',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cloudFunctionsInstance.post('/updateConferenceOrganizer', payload)
      toast.success(response.data?.msg || 'Organizer updated.')
      return { uid: payload.uid, ...payload }
    } catch (err) {
      const msg =
        err.response?.data?.msg || 'Something went wrong updating the organizer. Please try again.'
      toast.error(msg)
      return rejectWithValue(msg)
    }
  }
)

const organizersSlice = createSlice({
  name: 'organizers',
  initialState: {
    organizers: [],
    inProgress: false,
    error: null
  },
  reducers: {},
  extraReducers: {
    [fetchAllOrganizers.pending]: (state) => {
      state.inProgress = true
      state.error = null
    },
    [fetchAllOrganizers.fulfilled]: (state, action) => {
      state.inProgress = false
      state.organizers = action.payload
    },
    [fetchAllOrganizers.rejected]: (state, action) => {
      state.inProgress = false
      state.error = action.payload
    },
    [createOrganizer.fulfilled]: () => {
      // List will be re-fetched by the component after creation
    },
    [deleteOrganizer.fulfilled]: (state, action) => {
      state.organizers = state.organizers.filter((o) => o.uid !== action.payload.uid)
    },
    [updateOrganizer.fulfilled]: (state, action) => {
      const idx = state.organizers.findIndex((o) => o.uid === action.payload.uid)
      if (idx !== -1) {
        state.organizers[idx] = { ...state.organizers[idx], ...action.payload }
      }
    },
    [signOut.fulfilled]: (state) => {
      state.organizers = []
      state.inProgress = false
      state.error = null
    }
  }
})

export default organizersSlice
